import React, { useCallback, useEffect, useState } from 'react';
import { APP_TIMEZONE } from '../../config';
import { useAdminApi, errorMessage } from './adminApi';
import { formatDate } from './format';
import { OFFICE_LABELS } from '../council/offices';
import { btnDanger, btnPrimary, btnSecondary, inputClass, labelClass, selectClass } from './AdminUI';

const MODES = [
  { value: 'off', label: 'Off', description: 'Nobody sees any of it. Nothing is deleted.' },
  {
    value: 'preview',
    label: 'Admin preview',
    description: 'Only admins see and use it: the Council portal (acting as a council member to test), the new approval types and agent tools. Residents and council see nothing and get no emails about it.'
  },
  { value: 'on', label: 'On', description: 'Council members get the Council portal.' }
];

const todayInputValue = () => new Date().toLocaleDateString('en-CA', { timeZone: APP_TIMEZONE });

function CouncilSetup({ condo, notify }) {
  const api = useAdminApi();
  const [members, setMembers] = useState(null);
  const [residents, setResidents] = useState([]);
  const [form, setForm] = useState({ userId: '', office: 'member', electedAt: todayInputValue() });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [council, users] = await Promise.all([
        api.get(`/api/admin/condos/${condo._id}/council`),
        api.get('/api/admin/users', { condo: condo._id })
      ]);
      setMembers(council.data.members);
      setResidents((users.data.users || []).filter((u) => u.isActive));
    } catch (err) {
      alert(errorMessage(err, 'Failed to load council'));
    }
  }, [api, condo._id]);

  useEffect(() => { load(); }, [load]);

  const run = async (request, message) => {
    setBusy(true);
    try {
      await request();
      notify(message);
      load();
    } catch (err) {
      alert(errorMessage(err, 'Failed to update council'));
    } finally {
      setBusy(false);
    }
  };

  const add = (e) => {
    e.preventDefault();
    if (!form.userId) return;
    run(() => api.post(`/api/admin/condos/${condo._id}/council`, form), 'Council member added.');
    setForm({ userId: '', office: 'member', electedAt: form.electedAt });
  };

  const seatedIds = new Set((members || []).map((m) => m.user._id));

  return (
    <section className="mt-6 pt-4 border-t border-border">
      <h3 className="text-base font-semibold text-foreground">Council</h3>
      <p className="text-sm text-muted-foreground mb-3">Council is elected at every AGM. End the outgoing members' terms and add the new council here.</p>
      {!members ? null : members.length === 0 ? (
        <p className="text-sm text-muted-foreground mb-3">No council members yet.</p>
      ) : (
        <ul className="space-y-2 mb-4">
          {members.map((m) => (
            <li key={m._id} className="flex flex-wrap items-center gap-2 p-2 rounded border border-border text-sm">
              <span className="font-medium text-foreground mr-auto">{m.user.name} <span className="text-muted-foreground font-normal">· elected {formatDate(m.electedAt)}</span></span>
              <select
                aria-label={`Office of ${m.user.name}`}
                value={m.office}
                disabled={busy}
                onChange={(e) => run(() => api.put(`/api/admin/condos/${condo._id}/council/${m._id}`, { office: e.target.value }), 'Office updated.')}
                className={selectClass}
              >
                {Object.entries(OFFICE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <button
                type="button"
                disabled={busy}
                onClick={() => window.confirm(`End ${m.user.name}'s term on council today?`)
                  && run(() => api.put(`/api/admin/condos/${condo._id}/council/${m._id}`, { endedAt: todayInputValue() }), 'Term ended.')}
                className={btnDanger}
              >
                End term
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
        <div className="md:col-span-2">
          <label htmlFor="council-resident" className={labelClass}>Resident</label>
          <select id="council-resident" value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} className={inputClass}>
            <option value="">Pick a resident...</option>
            {residents.filter((u) => !seatedIds.has(u._id)).map((u) => (
              <option key={u._id} value={u._id}>{u.name}{u.unitRef?.number ? ` (unit ${u.unitRef.number})` : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="council-office" className={labelClass}>Office</label>
          <select id="council-office" value={form.office} onChange={(e) => setForm({ ...form, office: e.target.value })} className={inputClass}>
            {Object.entries(OFFICE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="council-elected" className={labelClass}>Elected (AGM date)</label>
          <input id="council-elected" type="date" value={form.electedAt} onChange={(e) => setForm({ ...form, electedAt: e.target.value })} className={inputClass} />
        </div>
        <div className="md:col-span-4">
          <button type="submit" disabled={busy || !form.userId} className={btnSecondary}>Add to council</button>
        </div>
      </form>
    </section>
  );
}

// The switch that keeps self-management hidden until launch (context/SelfManagement.md).
function SelfManagementSettings({ condo, notify, onChanged }) {
  const api = useAdminApi();
  const savedMode = condo.selfManagement?.mode || 'off';
  const [mode, setMode] = useState(savedMode);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const response = await api.put(`/api/admin/condos/${condo._id}/self-management`, { mode });
      notify('Self-management updated.');
      onChanged(response.data.condo);
    } catch (err) {
      alert(errorMessage(err, 'Failed to update self-management'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-labelledby="self-management-title" className="border-t border-border pt-4 mb-4">
      <h3 id="self-management-title" className="text-base font-semibold text-foreground">Self-management</h3>
      <p className="text-sm text-muted-foreground mb-3">Council portal, council decisions and the agent tools that prepare them.</p>
      <div role="radiogroup" aria-label="Self-management mode" className="space-y-2">
        {MODES.map((option) => (
          <label key={option.value} className={`flex gap-3 items-start p-3 rounded-lg border cursor-pointer ${mode === option.value ? 'border-primary bg-primary/10' : 'border-border'}`}>
            <input type="radio" name="self-management-mode" value={option.value} checked={mode === option.value} onChange={() => setMode(option.value)} className="mt-1" />
            <span>
              <span className="block text-sm font-semibold text-foreground">{option.label}</span>
              <span className="block text-xs text-muted-foreground">{option.description}</span>
            </span>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        {condo.selfManagement?.changedAt && (
          <span className="text-xs text-muted-foreground">Changed {formatDate(condo.selfManagement.changedAt)}. The dev app copies this setting from live every night.</span>
        )}
        <button type="button" onClick={save} disabled={saving || mode === savedMode} className={`${btnPrimary} ml-auto`}>{saving ? 'Saving...' : 'Save'}</button>
      </div>
      {savedMode !== 'off' && <CouncilSetup condo={condo} notify={notify} />}
    </section>
  );
}

export default SelfManagementSettings;
